import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import AppSidebar from '../components/AppSidebar';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Separator } from '../components/ui/separator';
import { ScrollArea } from '../components/ui/scroll-area';
import { Skeleton } from '../components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { API_URL } from '../lib/apiBase';
import {
  ReceiptText,
  TrendingUp,
  Minus,
  TrendingDown,
  Clock,
  CheckCircle2,
  DollarSign,
  AlertCircle,
} from 'lucide-react';

const OUTCOMES = [
  { value: 'win', label: 'Win', icon: TrendingUp, color: 'text-emerald-600', ring: 'data-[active=true]:border-emerald-500 data-[active=true]:bg-emerald-50 dark:data-[active=true]:bg-emerald-950/30' },
  { value: 'neutral', label: 'Neutral', icon: Minus, color: 'text-amber-600', ring: 'data-[active=true]:border-amber-500 data-[active=true]:bg-amber-50 dark:data-[active=true]:bg-amber-950/30' },
  { value: 'miss', label: 'Miss', icon: TrendingDown, color: 'text-rose-600', ring: 'data-[active=true]:border-rose-500 data-[active=true]:bg-rose-50 dark:data-[active=true]:bg-rose-950/30' },
];

const outcomeMeta = (o) => OUTCOMES.find((x) => x.value === o) || OUTCOMES[1];

const StatCard = ({ icon: Icon, label, value, tone, testId }) => (
  <Card className="p-4 flex items-center gap-3" data-testid={testId}>
    <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${tone}`}>
      <Icon className="h-5 w-5" />
    </div>
    <div className="min-w-0">
      <div className="text-2xl font-bold tabular-nums leading-none">{value}</div>
      <div className="text-xs text-muted-foreground mt-1">{label}</div>
    </div>
  </Card>
);

const ReceiptsPage = () => {
  const { currentWorkspaceId } = useWorkspace();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);

  // Log-impact dialog state
  const [logging, setLogging] = useState(null); // the item being logged
  const [mrrDelta, setMrrDelta] = useState('');
  const [outcome, setOutcome] = useState('win');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchReceipts = useCallback(async () => {
    if (!currentWorkspaceId) return;
    try {
      setLoading(true);
      const { data } = await axios.get(`${API_URL}/api/receipts`, {
        params: { workspace_id: currentWorkspaceId },
      });
      setItems(data.items || []);
      setSummary(data.summary || null);
    } catch (e) {
      console.error('Failed to load receipts', e);
      toast.error('Failed to load decision receipts');
    } finally {
      setLoading(false);
    }
  }, [currentWorkspaceId]);

  useEffect(() => {
    fetchReceipts();
  }, [fetchReceipts]);

  const openLog = (item) => {
    setLogging(item);
    if (item.receipt) {
      setMrrDelta(String(item.receipt.mrr_delta ?? ''));
      setOutcome(item.receipt.outcome || 'win');
      setNote(item.receipt.note || '');
    } else {
      setMrrDelta('');
      setOutcome('win');
      setNote('');
    }
  };

  const submitLog = async () => {
    if (!logging) return;
    const parsed = parseFloat(mrrDelta);
    if (Number.isNaN(parsed)) {
      toast.error('Enter the MRR change (use 0 if none)');
      return;
    }
    try {
      setSaving(true);
      await axios.post(`${API_URL}/api/receipts/${logging.signal_id}/log`, {
        mrr_delta: parsed,
        outcome,
        note,
      });
      toast.success('Receipt logged — decision now has proof');
      setLogging(null);
      fetchReceipts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to log receipt');
    } finally {
      setSaving(false);
    }
  };

  const fmtDate = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
    } catch (_e) {
      return '—';
    }
  };

  const netMrr = summary?.net_mrr_delta ?? 0;

  return (
    <div className="min-h-screen bg-background flex" data-testid="receipts-shell">
      <AppSidebar />
      <div className="flex-1 min-w-0 flex flex-col">
        {/* Header */}
        <div className="border-b bg-card sticky top-0 z-40">
          <div className="mx-auto px-4 sm:px-6 py-4">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                <ReceiptText className="h-5 w-5" />
              </div>
              <div>
                <h1 className="text-lg font-bold leading-tight" data-testid="receipts-title">
                  Decision Receipts
                </h1>
                <p className="text-xs text-muted-foreground">
                  Proof of what shipping actually did — 30 days after you ship.
                </p>
              </div>
            </div>
          </div>
        </div>

        <ScrollArea className="flex-1">
          <div className="mx-auto max-w-5xl px-4 sm:px-6 py-6 space-y-6">
            {/* Summary cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {loading ? (
                Array.from({ length: 4 }).map((_, i) => (
                  <Skeleton key={i} className="h-[76px] rounded-xl" />
                ))
              ) : (
                <>
                  <StatCard
                    icon={DollarSign}
                    label="Net MRR from shipped"
                    value={`${netMrr >= 0 ? '+' : ''}$${Math.round(netMrr)}`}
                    tone={netMrr >= 0 ? 'bg-emerald-500/10 text-emerald-600' : 'bg-rose-500/10 text-rose-600'}
                    testId="receipts-stat-mrr"
                  />
                  <StatCard
                    icon={CheckCircle2}
                    label="Receipts logged"
                    value={summary?.logged ?? 0}
                    tone="bg-primary/10 text-primary"
                    testId="receipts-stat-logged"
                  />
                  <StatCard
                    icon={Clock}
                    label="Follow-ups due"
                    value={summary?.due ?? 0}
                    tone="bg-amber-500/10 text-amber-600"
                    testId="receipts-stat-due"
                  />
                  <StatCard
                    icon={TrendingUp}
                    label="Wins / Neutral / Miss"
                    value={`${summary?.wins ?? 0}/${summary?.neutrals ?? 0}/${summary?.misses ?? 0}`}
                    tone="bg-sky-500/10 text-sky-600"
                    testId="receipts-stat-outcomes"
                  />
                </>
              )}
            </div>

            {/* List */}
            {loading ? (
              <div className="space-y-3">
                {Array.from({ length: 3 }).map((_, i) => (
                  <Skeleton key={i} className="h-24 rounded-xl" />
                ))}
              </div>
            ) : items.length === 0 ? (
              <Card className="p-10 text-center" data-testid="receipts-empty">
                <div className="mx-auto h-12 w-12 rounded-full bg-muted flex items-center justify-center mb-3">
                  <ReceiptText className="h-6 w-6 text-muted-foreground" />
                </div>
                <h3 className="font-semibold">No receipts yet</h3>
                <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
                  Ship a decision from the Roadmap and VibeSprint will follow up 30 days
                  later so you can record the real MRR impact — decisions with receipts.
                </p>
              </Card>
            ) : (
              <div className="space-y-3" data-testid="receipts-list">
                {items.map((item) => {
                  const isLogged = item.receipt_status === 'logged';
                  const isDue = item.receipt_status === 'due';
                  const om = item.receipt ? outcomeMeta(item.receipt.outcome) : null;
                  return (
                    <motion.div
                      key={item.signal_id}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <Card className="p-4" data-testid={`receipt-card-${item.signal_id}`}>
                        <div className="flex items-start justify-between gap-4 flex-wrap">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap mb-1">
                              {isLogged && om ? (
                                <Badge className="gap-1" variant="secondary" data-testid={`receipt-outcome-${item.signal_id}`}>
                                  <om.icon className={`h-3 w-3 ${om.color}`} />
                                  {om.label}
                                </Badge>
                              ) : isDue ? (
                                <Badge variant="secondary" className="gap-1 text-amber-600">
                                  <AlertCircle className="h-3 w-3" /> Follow-up due
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="gap-1 text-muted-foreground">
                                  <Clock className="h-3 w-3" />
                                  {typeof item.days_left === 'number'
                                    ? `${Math.max(0, item.days_left)}d until follow-up`
                                    : 'Tracking'}
                                </Badge>
                              )}
                              <span className="text-[11px] text-muted-foreground">
                                Shipped {fmtDate(item.shipped_at)}
                              </span>
                            </div>
                            <h3 className="font-semibold leading-snug">{item.summary}</h3>
                            {item.expected_outcome && (
                              <p className="text-xs text-muted-foreground mt-1">
                                <span className="font-medium">Predicted:</span> {item.expected_outcome}
                              </p>
                            )}
                            {isLogged && item.receipt && (
                              <div className="mt-2 flex items-center gap-3 text-sm">
                                <span className={`font-semibold tabular-nums ${item.receipt.mrr_delta >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                                  {item.receipt.mrr_delta >= 0 ? '+' : ''}${item.receipt.mrr_delta}/mo actual
                                </span>
                                {item.receipt.note && (
                                  <span className="text-muted-foreground truncate">“{item.receipt.note}”</span>
                                )}
                              </div>
                            )}
                          </div>
                          <div className="flex-shrink-0">
                            <Button
                              variant={isLogged ? 'outline' : 'default'}
                              size="sm"
                              onClick={() => openLog(item)}
                              data-testid={`receipt-log-btn-${item.signal_id}`}
                            >
                              {isLogged ? 'Edit impact' : 'Log impact'}
                            </Button>
                          </div>
                        </div>
                      </Card>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </div>
        </ScrollArea>
      </div>

      {/* Log impact dialog */}
      <Dialog open={!!logging} onOpenChange={(o) => !o && setLogging(null)}>
        <DialogContent data-testid="receipt-log-dialog">
          <DialogHeader>
            <DialogTitle>Log 30-day impact</DialogTitle>
            <DialogDescription className="truncate">
              {logging?.summary}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-1">
            <div className="space-y-1.5">
              <Label>Outcome</Label>
              <div className="grid grid-cols-3 gap-2">
                {OUTCOMES.map((o) => (
                  <button
                    key={o.value}
                    type="button"
                    data-active={outcome === o.value}
                    onClick={() => setOutcome(o.value)}
                    className={`flex flex-col items-center gap-1 rounded-lg border-2 border-border py-2.5 text-sm transition-colors hover:bg-muted ${o.ring}`}
                    data-testid={`receipt-outcome-option-${o.value}`}
                  >
                    <o.icon className={`h-4 w-4 ${o.color}`} />
                    {o.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="mrr-delta">MRR change (USD / month)</Label>
              <div className="relative">
                <DollarSign className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="mrr-delta"
                  type="number"
                  step="1"
                  placeholder="e.g. 120 (use a negative number if it hurt MRR)"
                  className="pl-8"
                  value={mrrDelta}
                  onChange={(e) => setMrrDelta(e.target.value)}
                  data-testid="receipt-mrr-input"
                />
              </div>
              <p className="text-[11px] text-muted-foreground">
                The real monthly recurring revenue this shipped decision moved.
              </p>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="receipt-note">Note (optional)</Label>
              <Textarea
                id="receipt-note"
                placeholder="What actually happened? e.g. Converted 3 trials, 1 upsell."
                rows={3}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                data-testid="receipt-note-input"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setLogging(null)} data-testid="receipt-cancel-btn">
              Cancel
            </Button>
            <Button onClick={submitLog} disabled={saving} data-testid="receipt-save-btn">
              {saving ? 'Saving…' : 'Save receipt'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ReceiptsPage;
